import type {
  CustomValidator,
  ErrorTransformer,
  FormValidation,
  RJSFSchema,
} from '@rjsf/utils';
import { customizeValidator } from '@rjsf/validator-ajv8';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SchemaFormData } from './utils';
import { isCurrencyField, isMultiFileField } from './utils';

const MATCH_END_DATE = /^(.+)EndDate$/;

export const useCustomValidation = (
  schema: RJSFSchema,
  onValidate?: (formData: SchemaFormData, errors: FormValidation) => void
) => {
  const { t } = useTranslation();

  const requiredFieldMessage = t('phrases.requiredField', {
    defaultValue: 'This field cannot be blank',
  });

  const requiredFileMessage = t('phrases.requiredFile', {
    defaultValue: 'Please select a file',
  });

  const customValidate: CustomValidator = useCallback(
    (formData: SchemaFormData, errors: FormValidation) => {
      onValidate?.(formData, errors);

      for (const key of Object.keys(formData)) {
        const fieldValue = formData[key];

        // Ensure any end date is later than the matching start date
        if (MATCH_END_DATE.test(key)) {
          const prefix = key.match(MATCH_END_DATE)?.[1];
          const startDateKey = `${prefix}StartDate`;
          const startDate = formData[startDateKey];
          const endDate = fieldValue;
          if (
            typeof startDate === 'string' &&
            typeof endDate === 'string' &&
            startDate > endDate
          ) {
            errors[key]?.addError(
              t('phrases.endDateMustBeLater', {
                defaultValue: 'End date must be later than start date',
              })
            );
          }
        }

        // Mark currency object fields as blank if they are required
        // and the inner `currency` or `amount` fields are empty
        const propertySchema = schema.properties?.[key] as
          | RJSFSchema
          | undefined;
        if (
          schema.required?.includes(key) &&
          propertySchema &&
          isCurrencyField(propertySchema) &&
          fieldValue != null &&
          typeof fieldValue === 'object' &&
          !Array.isArray(fieldValue)
        ) {
          const currencyValue = fieldValue;
          if (
            currencyValue.amount === undefined ||
            currencyValue.currency === undefined
          ) {
            errors[key]?.addError(requiredFieldMessage);
          }
        }

        // Mark checkbox as blank if they are required
        // and the checkbox value is false
        if (
          schema.required?.includes(key) &&
          propertySchema?.type === 'boolean' &&
          fieldValue === false &&
          propertySchema.format !== 'radio'
        ) {
          errors[key]?.addError(requiredFieldMessage);
        }

        // Mark multi-file upload fields as blank if they are required
        // and the file array is empty
        if (
          schema.required?.includes(key) &&
          propertySchema &&
          isMultiFileField(propertySchema) &&
          Array.isArray(fieldValue) &&
          fieldValue.length === 0
        ) {
          errors[key]?.addError(requiredFileMessage);
        }

        // ensure uri starts with https://, ignoring fields left empty
        if (
          propertySchema?.format === 'uri' &&
          typeof fieldValue === 'string' &&
          fieldValue !== '' &&
          !fieldValue.startsWith('https://')
        ) {
          errors[key]?.addError(t('phrases.mustStartWithHttps'));
        }
      }
      return errors;
    },
    [
      onValidate,
      schema.properties,
      schema.required,
      t,
      requiredFieldMessage,
      requiredFileMessage,
    ]
  );

  const transformErrors: ErrorTransformer = useCallback(
    (errors) => {
      return errors.map((error) => {
        if (error.name === 'required') {
          const propertySchema = schema.properties?.[
            error.property as string
          ] as RJSFSchema;

          // Custom required field messages
          error.message =
            propertySchema?.format === 'data-url'
              ? requiredFileMessage
              : requiredFieldMessage;
        }
        if (error.name === 'format' && error.params.format === 'uri') {
          error.message = t('phrases.mustBeValidUrl');
        }
        if (error.name === 'pattern') {
          error.message = t('phrases.invalidFormat');
        }
        return error;
      });
    },
    [schema, requiredFileMessage, requiredFieldMessage, t]
  );

  const validator = useMemo(
    () =>
      customizeValidator({
        customFormats: {
          'rich-text': /.*/,
          'sub-price': /.+/,
          checkboxes: /.+/,
          pricing: /.+/,
          radio: /.+/,
          textarea: /.*/,
          collapsible: /.+/,
          country: /.+/,
          subdivision: /.+/,
          icon: /.+/,
          phone: /.+/,
        },
      }),
    []
  );

  return { customValidate, transformErrors, validator };
};
